import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Camera, LogOut, Settings, Pencil, Crown, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, compressImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { PositionPicker } from "@/components/PositionPicker";
import { InstallPrompt } from "@/components/InstallPrompt";
import { PushToggle } from "@/components/PushToggle";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { POS_LABEL, pct, fmtDate } from "@/lib/football";

const TILE_TONES = ["bg-cyan-50 text-cyan-800", "bg-lime-50 text-lime-800", "bg-slate-100 text-slate-700", "bg-pink-50 text-pink-800", "bg-violet-50 text-violet-800", "bg-emerald-50 text-emerald-800", "bg-orange-50 text-orange-800", "bg-sky-50 text-sky-800", "bg-amber-50 text-amber-800"];
const YEARS = Array.from({ length: new Date().getFullYear() - 1979 }, (_, i) => new Date().getFullYear() - i);
export const yearsLabel = (n) => (n > 0 ? `${n} an${n > 1 ? "s" : ""}` : "moins d'un an");

const Tile = ({ label, value, testid, i = 0 }) => (
  <div className={`rounded-2xl p-3 text-center ${TILE_TONES[i % TILE_TONES.length]}`}>
    <p data-testid={testid} className="num text-xl font-bold">{value}</p>
    <p className="text-[10px] opacity-75 mt-0.5">{label}</p>
  </div>
);

const EditProfile = ({ open, onClose }) => {
  const { user, setUser } = useAuth();
  const [f, setF] = useState(null);
  useEffect(() => {
    if (open) setF({ first_name: user.first_name, last_name: user.last_name, primary_position: user.primary_position, secondary_positions: user.secondary_positions, start_year: user.start_year || "" });
  }, [open, user]);
  const save = async () => {
    try {
      const r = await api.put("/users/me", { ...f, start_year: f.start_year ? +f.start_year : null });
      setUser(r.data);
      toast.success("Profil mis à jour");
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="bg-white text-slate-900 max-w-md mx-auto">
        {f && (
          <div className="px-5 pb-6 overflow-y-auto max-h-[85dvh] space-y-4" data-testid="edit-profile-sheet">
            <DrawerTitle className="font-display text-xl">Modifier le profil</DrawerTitle>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="edit-first-name" className="gg-input" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} placeholder="Prénom" />
              <input data-testid="edit-last-name" className="gg-input" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} placeholder="Nom" />
            </div>
            <p className="text-xs font-bold text-slate-500 uppercase">Année de début chez Gogoats</p>
            <select data-testid="edit-start-year" className="gg-input" value={f.start_year} onChange={(e) => setF({ ...f, start_year: e.target.value })}>
              <option value="">— Choisir —</option>
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <p className="text-xs font-bold text-slate-500 uppercase">Position principale</p>
            <PositionPicker value={f.primary_position} onChange={(v) => setF({ ...f, primary_position: v, secondary_positions: f.secondary_positions.filter((s) => s !== v) })} testid="edit-primary" />
            <p className="text-xs font-bold text-slate-500 uppercase">Peut également jouer</p>
            <PositionPicker multi value={f.secondary_positions} exclude={f.primary_position} onChange={(v) => setF({ ...f, secondary_positions: v })} testid="edit-secondary" />
            <button data-testid="edit-profile-save" onClick={save} className="w-full rounded-2xl bg-cyan-400 text-zinc-950 font-display font-black py-4">ENREGISTRER</button>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default function Profile() {
  const { id } = useParams();
  const { user, setUser, logout } = useAuth();
  const pid = id || user.id;
  const own = pid === user.id;
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [season, setSeason] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    api.get(`/users/${pid}`, { params: season ? { season } : {} }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [pid, user, season]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const avatar = await compressImage(file);
      const r = await api.put("/users/me/avatar", { avatar });
      setUser(r.data);
      toast.success("Photo mise à jour");
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (!d) return <div className="h-96 rounded-3xl bg-white shadow-sm animate-pulse" />;
  const p = d.player;
  return (
    <div data-testid="profile-page" className="space-y-5">
      <div className="flex flex-col items-center text-center pt-2">
        <div className="relative">
          <Avatar user={own ? { ...p, avatar_url: user.avatar_url } : p} className="w-28 h-28 text-3xl" ring="border-4 border-white shadow-lg shadow-cyan-500/30" testid="profile-avatar" />
          {own && (
            <button data-testid="change-photo-button" onClick={() => fileRef.current.click()} className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-cyan-400 text-zinc-950 flex items-center justify-center border-4 border-[#F2F5FA]">
              <Camera className="w-4 h-4" />
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="photo-input" />
        </div>
        <h1 data-testid="profile-full-name" className="font-display text-2xl font-extrabold mt-3 uppercase">{p.first_name} {p.last_name}</h1>
        <p className="text-sm text-cyan-600 font-semibold">{POS_LABEL[p.primary_position]}</p>
        {p.secondary_positions.length > 0 && <p className="text-xs text-slate-500 mt-0.5">Peut aussi jouer : {p.secondary_positions.map((s) => POS_LABEL[s]).join(", ")}</p>}
        <div className="flex gap-2 justify-center mt-2 flex-wrap">
          <span data-testid="profile-seniority" className="rounded-full bg-gradient-to-r from-orange-400 to-pink-500 text-white px-3 py-1 text-xs font-bold">
            {p.start_year ? `Ancienneté : ${yearsLabel(p.seniority)} · depuis ${p.start_year}` : "Ancienneté non renseignée"}
          </span>
          <span className="num rounded-full bg-cyan-100 text-cyan-800 px-3 py-1 text-xs font-bold" data-testid="profile-rank">{p.played ? `${p.rank}ᵉ au classement général` : "Pas encore de match joué"}</span>
          {p.football_experience_years !== null && p.football_experience_years !== undefined && (
            <span data-testid="profile-football-xp" className="rounded-full bg-lime-100 text-lime-800 px-3 py-1 text-xs font-bold">Football : {yearsLabel(p.football_experience_years)} d'expérience</span>
          )}
          {p.goals > 0 && <span className="num rounded-full bg-orange-100 text-orange-800 px-3 py-1 text-xs font-bold" data-testid="profile-rank-goals">{p.rank_goals}ᵉ buteur</span>}
          {p.assists > 0 && <span className="num rounded-full bg-violet-100 text-violet-800 px-3 py-1 text-xs font-bold" data-testid="profile-rank-assists">{p.rank_assists}ᵉ passeur</span>}
        </div>
        {own && <button data-testid="edit-profile-button" onClick={() => setEdit(true)} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white shadow-sm border border-slate-200 px-4 py-2 text-xs font-semibold"><Pencil className="w-3.5 h-3.5" /> Modifier le profil</button>}
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="season-selector">
        {d.seasons.map((y) => (
          <button key={y} data-testid={`season-${y}`} onClick={() => setSeason(y)}
            className={`rounded-full px-4 py-1.5 text-xs font-bold border ${p.season === y ? "bg-cyan-400 border-cyan-300 text-zinc-950" : "bg-white border-slate-200"}`}>Saison {y}</button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2" data-testid="profile-stats">
        <Tile i={0} label="Matchs" value={p.played} testid="stat-played" />
        <Tile i={1} label="Victoires" value={p.wins} testid="stat-wins" />
        <Tile i={2} label="Nuls" value={p.draws} testid="stat-draws" />
        <Tile i={3} label="Défaites" value={p.losses} testid="stat-losses" />
        <Tile i={4} label="Points" value={p.points} testid="stat-points" />
        <Tile i={5} label="% victoires" value={pct(p.win_rate)} testid="stat-winrate" />
        <Tile i={6} label="Buts" value={p.goals} testid="stat-goals" />
        <Tile i={7} label="Passes déc." value={p.assists} testid="stat-assists" />
        <Tile i={8} label="Homme du Match" value={p.motm} testid="stat-motm" />
        <Tile i={0} label="Contributions offensives" value={p.goal_contributions} testid="stat-contributions" />
      </div>
      <div className="rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-4" data-testid="captain-stats">
        <p className="text-[11px] font-black tracking-[0.2em] text-amber-600 flex items-center gap-1.5"><Crown className="w-4 h-4" /> CAPITAINE</p>
        <div className="grid grid-cols-5 gap-1 mt-3 text-center">
          {[["Matchs", p.cap_played], ["V", p.cap_wins], ["N", p.cap_draws], ["D", p.cap_losses], ["%V", pct(p.cap_win_rate)]].map(([l, v]) => (
            <div key={l}><p className="num text-base font-bold">{v}</p><p className="text-[10px] text-slate-500">{l}</p></div>
          ))}
        </div>
      </div>
      {d.history.length > 0 && (
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Derniers matchs</p>
          <div className="space-y-1.5">
            {d.history.map((h) => (
              <Link key={h.match_id} to={`/matchs/${h.match_id}`} className="flex items-center gap-3 rounded-2xl bg-white shadow-sm border border-slate-200 px-3 py-2.5">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-black ${h.result === "W" ? "bg-emerald-500 text-white" : h.result === "D" ? "bg-slate-300" : "bg-red-500 text-white"}`}>{h.result === "W" ? "V" : h.result === "D" ? "N" : "D"}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{fmtDate(h.date)}</p>
                  <p className="num text-[10px] text-slate-500">{h.team === "white" ? "Blanc" : "Noir"} · {h.white_score}-{h.black_score} · {h.position || "—"}{h.is_captain ? " · Capitaine" : ""}</p>
                </div>
                <span className="num text-xs text-slate-600">{h.goals}B {h.assists}PD</span>
                {h.motm && <Trophy className="w-4 h-4 text-amber-600" />}
              </Link>
            ))}
          </div>
        </div>
      )}
      {own && (
        <div className="space-y-2.5">
          <InstallPrompt force />
          <PushToggle />
          {user.role === "admin" && (
            <Link to="/admin" data-testid="admin-link" className="flex items-center gap-3 rounded-2xl bg-cyan-400 text-zinc-950 px-4 py-4 font-display font-black">
              <Settings className="w-5 h-5" /> ADMINISTRATION
            </Link>
          )}
          <button data-testid="logout-button" onClick={logout} className="w-full flex items-center justify-center gap-2 rounded-2xl bg-white shadow-sm border border-slate-200 py-3.5 text-sm text-slate-600">
            <LogOut className="w-4 h-4" /> Se déconnecter
          </button>
        </div>
      )}
      <EditProfile open={edit} onClose={() => setEdit(false)} />
    </div>
  );
}
